import { SortDirection } from 'src/app/components/sort/sort.types';
import { ICD, Professional } from 'src/app/shared/interfaces/core.interface';

import moment from 'moment';

import { ListAPIResponse } from 'src/app/core/api.service';
import { DocumentType } from 'src/app/shared/model/document-type';
import { ResponseSubReason } from 'src/app/shared/model/refusal-reasons.model';
import { Warning } from 'src/app/shared/model/warning';
import * as File from './file.model';
import { Person } from './person';
import { ProfessionalDetailsIf } from './professional';
import { Team } from './team';
import { IISODateTime } from '../interfaces/date.interfaces';
import { ILocation, ILocationGroup } from '../interfaces/location.interfaces';

import {
  IInterval,
  IProfessional,
  ISituation,
} from 'src/app/components/form-components/entities/form-components.interfaces';

export type SickNoteStatusType =
  | 'pending'
  | 'filled'
  | 'verified'
  | 'warning'
  | 'valid'
  | 'invalid'
  | 'triage';

/**
 * Whether a person has decided the document: `valid` is Aprovado and `invalid`
 * is Recusado. Not derivable from `fields.verified`, which `reprove` does not
 * persist -- a refused document keeps `verified = false`, so anything that means
 * "nothing left to do here" has to read the status.
 *
 * A function taking an optional status rather than an array to call `.includes`
 * on: the callers all hold `sickNote?.status`, and under `strictNullChecks` --
 * which the Betterer ratchet enforces even though the build has it off -- that
 * is not assignable to `SickNoteStatusType`. Widening the array to accept
 * `undefined` would have hidden the looseness instead of removing it.
 */
export const isDecidedStatus = (status?: SickNoteStatusType): boolean =>
  status === 'valid' || status === 'invalid';

export const SickNoteStatus: {
  [key in SickNoteStatusType]: {
    key: SickNoteStatusType;
    label: string;
    icon: string;
  };
} = {
  pending: {
    key: 'pending',
    label: 'Recebido',
    icon: 'schedule',
  },
  filled: {
    key: 'filled',
    label: 'Processando',
    icon: 'schedule',
  },
  verified: {
    key: 'verified',
    label: 'Em análise',
    icon: 'schedule',
  },
  warning: {
    key: 'warning',
    label: 'Em análise',
    icon: 'schedule',
  },
  valid: {
    key: 'valid',
    label: 'Aprovado',
    icon: 'thumb_up',
  },
  invalid: {
    key: 'invalid',
    label: 'Recusado',
    icon: 'thumb_down',
  },
  triage: {
    key: 'triage',
    label: 'Ambulatório',
    icon: 'local_hospital',
  },
} as const;

export interface SickNoteSync {
  success: boolean;
  error?: {
    code?: string;
    message: string;
  };
  vendor: string;
}

export interface SickNoteType {
  children: SickNoteType[];
  code: string;
  description: string;
  document_type: DocumentType;
  esocial_id: any;
  id: number;
  name: string;
  parent_id: number;
  visible: boolean;
  visible_b2c: boolean;
  situations: Situation[];
  active: boolean;
}

export interface ISickNoteFields {
  travel_minutes?: TravelMinutes;
  appointment_date?: string;
  expiration_date?: string;
  icds: ICD[];
  image_path?: string;
  link?: string;
  internal_notes?: string;
  public_notes?: {
    message?: string;
    reasons?: string[];
  };
  person_id: string;
  premises?: string;
  reasons?: ResponseSubReason[];
  secret?: string;
  professional?: Professional;
  type_id: number;
  vaccine?: {
    dose: string;
    name: string;
  };
  files: File.Data[];
  verified: boolean;
  external_id: string;
}

export interface Metadata {
  created_at?: string;
  updated_at?: string;
  registered_by_uid: string;
  updated_by_uid: string;
  updated_by?: {
    id: number;
    name: string;
    email: string;
    user_id: string;
  };
  source: string;
}

export interface IExtendedSickNote {
  id: string;
  company_id: string;
  status: SickNoteStatusType;
  short_id: string;
  details: {
    alerts: {
      [key: string]: boolean;
    };
    issues: {
      [key: string]: any;
    };
  };
  fields: ISickNoteFields;
  extras: {
    document_type: DocumentType;
    location: Location;
    person: Person;
    image_url: string;
    type: SickNoteType;
    metadata: Metadata;
    status_reasons?: StatusReason[];
    professional: ProfessionalDetailsIf;
    related: Related;
    situations: Situation[];
    team: Team;
    syncs: SickNoteSync[];
    ai_extracted_data: Record<string, string | null> | null;
    /**
     * What the Platform pipeline reported for the note's latest submission.
     * Emitted by the v4 read as a list — `[]` when that submission ran no
     * active stage, so a note processed before the capture landed reads as
     * empty rather than absent. Until the read that produces it is deployed
     * the key is missing altogether, which is why it is optional here and why
     * the readers treat absent and empty the same way. Only `ACTIVE` stages
     * are exposed, so a `SHADOW` one cannot raise an operator-facing card.
     *
     * That last sentence is a fact about `business_care`, and what this app
     * does when it stops being true is recorded on the tech-matrix card under
     * "Cross-service invariants the frontend relies on" rather than argued
     * here: https://github.com/closecare/tech-matrix/blob/main/repositories/rh/web-b2b.md
     */
    platform_stages?: PlatformStage[];
  };
}

export interface PlatformStage {
  /** Open vocabulary: 'DEDUP', 'LEGIBILITY', and whatever the pipeline adds. */
  kind: string;
  name: string;
  mode: string;
  /** Execution axis, not the finding: SUCCEEDED | FAILED | SKIPPED. */
  status: string;
  /** The finding. Null when the stage produced no output (FAILED/SKIPPED). */
  outcome: string | null;
  created_at: string;
  updated_at: string;
}

export type Related = Omit<IExtendedSickNote, 'extras' | 'details'> & {
  extras: {
    image_url: string;
    labor_union: {
      code: string;
      id: number;
      name: string;
    };
    location: ILocation;
    location_group: ILocationGroup;
    metadata: {
      source: 'employee' | 'manual' | 'batch';
      registered_by_uid: string;
      updated_by_uid: string;
      created_at: IISODateTime;
      updated_at: IISODateTime;
      updated_by: {
        id: number;
        email: string;
        name: string;
        user_id: string;
      };
    };
    person: Pick<
      Person,
      | 'id'
      | 'code'
      | 'cpf'
      | 'name'
      | 'document_id'
      | 'document_type'
      | 'social_name'
      | 'active'
      | 'status'
    >;
    professional: {
      id: number;
      name: string;
      state: string;
      status: string;
      type: string;
      specialty: string[];
      created_at: IISODateTime;
    };
    status_reasons: {
      description: string;
      id: string;
      public: boolean;
      status: string;
      threshold: string;
      value: string;
    }[];
    /**
     * @deprecated The method should not be used
     */
    situation: Pick<Situation, 'id' | 'code' | 'name'>;
    situations: (Situation & { type: string })[];
    sync: SickNoteSync;
    type: {
      id: number;
      code: string;
      name: string;
    };
    document_type: {
      id: number;
      code: string;
      label: string;
      family: string;
      social_security: boolean;
    };
    team: Team;
    history: {
      status: string;
      created_at: string;
      reasons: {
        id: string;
        status: string;
        threshold: string;
        value: string;
        public: boolean;
      }[];
      registered_by: {
        id: number;
        user_id: string;
        email: string;
        type: string;
        name: string;
      };
    };
  };
  details: {
    alerts: {
      is_late: boolean;
    };
    issues: { [key: string]: string };
    field_status: { [key: string]: 'pending' | 'warning' | 'verified' };
  };
};

export class TravelMinutes {
  before: number;
  after: number;
}

export class SickNoteBody {
  person_id: string;
  icds: string[];
  premises?: string;
  internal_notes?: string;
  type_id?: number;
  files?: string[] | [];
  issues?: { [key: string]: string };
  link?: string;
  secret?: string;
  public_notes?: PublicNotes;
  status?: SickNoteStatusType;
  reason_ids?: number[];
  related?: string;
  reason_rais_id?: number;
  vaccine_dose: number;
  vaccine_name: string;
  travel_minutes_before: number;
  travel_minutes_after: number;

  //TODO dynamic form - to remove
  appointment_date?: moment.Moment;
  expiration_date?: moment.Moment;
  professional?: Professional;
  situations: Situation[];

  //TODO dynamic form - added
  dynamicAppointmentDate: Date;
  dynamicInterval: IInterval;
  dynamicProfessional: IProfessional;
  dynamicSituations: ISituation[];
}

export interface PublicNotes {
  message: string;
  reasons: string[];
}

export interface StatusReason {
  id: string;
  status: string;
  threshold: string | number;
  value: string | number;
  description: string;
  public: boolean;
}

export interface Situation {
  id: number;
  code?: string;
  name?: string;
  granted_days_required?: boolean;
  granted_days?: number;
  start_date?: string;
  end_date?: string;
}

export interface RaisReason {
  code: string;
  id: number;
  label: string;
}

export const pendingStatus = ['verified', 'warning', 'triage'];
export function pendingValidation(status: SickNoteStatusType): boolean {
  return pendingStatus.includes(status);
}
export const inAnalysisStatus = ['pending', 'filled'];
export const processingStatus = ['filled'];

export interface WarningsAPIResponse extends ListAPIResponse {
  warnings: Warning[];
}

export class SickNotesAPIResponse implements ListAPIResponse {
  num_pages = 0;
  page = 0;
  total = 0;
  items: IExtendedSickNote[] = [];
  file?: {
    type: string;
    url: string;
  };
}

export class SickNotesV5APIResponse {
  items: IExtendedSickNote[] = [];
  file?: {
    type: string;
    url: string;
  };
}

export class SickNotesTypesAPIResponse implements ListAPIResponse {
  num_pages = 0;
  page = 0;
  sick_notes_types: SickNoteType[] = [];
  items: [];
  total = 0;
}

export type FieldDateType = 'appointment_date' | 'created_at' | 'updated_at';

export interface SickNoteChange {
  action: 'validate' | 'delete' | 'add' | 'update';
  sickNoteId: string;
  sickNoteStatus?: SickNoteStatusType;
  auto: boolean;
}

export interface SickNoteListRequest {
  direction?: 'next' | 'prev';
  first_item?: PageItem;
  last_item?: PageItem;
  obj_per_page?: number;
  filters?: any;
  verified?: boolean;
  /**
   * @deprecated
   */
  status?: string[];
  sort?: {
    field: string;
    order: SortDirection;
  }[];
}

export interface PageItem {
  id: string;
  appointment_date: string;
  created_at: string;
  updated_at: string;
}

export interface VaccineType {
  id: string;
  name: string;
  full_name: string;
}
